// src/pages/guides/ManualReconvsEASMGuide.js
// Manual Recon vs Managed EASM: The Real Cost Comparison - Guide G6

import React from 'react';
import useTrialCta from '../../hooks/useTrialCta';
import GuideByline from './GuideByline';
import GuideAnswer from './GuideAnswer';
import GuideFAQ, { buildFAQPageSchema } from './GuideFAQ';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/common/SEOHead';
import { PublicHeader, PublicFooter } from '../../components/common';
import './Guides.css';

function ManualReconvsEASMGuide() {
  const startTrial = useTrialCta('guide_trial:manual-recon-vs-easm');

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Manual Recon vs Managed EASM: The Real Cost Comparison",
    "description": "Compare the real costs of manual reconnaissance vs managed EASM. Time breakdown, skill requirements, and what manual recon misses.",
    "image": ["https://www.driftalarm.com/images/og-image.png"],
    "datePublished": "2026-03-06T00:00:00-05:00",
    "dateModified": "2026-08-26T00:00:00-04:00",
    "author": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com" },
    "publisher": { "@type": "Organization", "name": "DriftAlarm", "url": "https://www.driftalarm.com", "logo": { "@type": "ImageObject", "url": "https://www.driftalarm.com/images/og-image.png" } },
    "url": "https://www.driftalarm.com/guides/manual-recon-vs-easm",
    "mainEntityOfPage": "https://www.driftalarm.com/guides/manual-recon-vs-easm",
    "keywords": ["manual recon vs EASM", "EASM cost comparison", "EASM ROI", "attack surface management cost", "automated vs manual reconnaissance"]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.driftalarm.com" },
      { "@type": "ListItem", "position": 2, "name": "Security Guides", "item": "https://www.driftalarm.com/guides" },
      { "@type": "ListItem", "position": 3, "name": "Manual Recon vs EASM" }
    ]
  };

  return (
    <div className="guide-container">
      <SEOHead
        title="Manual Recon vs Managed EASM: Cost Guide | DriftAlarm"
        description="Compare the real costs of manual reconnaissance vs managed EASM. Time breakdown, skill requirements, and what manual recon misses."
        path="/guides/manual-recon-vs-easm"
        ogType="article"
        jsonLd={[articleSchema, breadcrumbSchema, buildFAQPageSchema('/guides/manual-recon-vs-easm')].filter(Boolean)}
      />
      <PublicHeader activeLink="guides" />

      <main className="guide-content">
        <section className="guide-hero">
          <h1>Manual Recon vs Managed EASM: The Real Cost Comparison</h1>
          <GuideByline path="/guides/manual-recon-vs-easm" />
          <GuideAnswer path="/guides/manual-recon-vs-easm" />
          <p className="guide-hero-intro">
            Manual reconnaissance is a valuable skill — and an expensive, time-consuming process
            when applied at scale. If you're weighing the cost of hiring consultants or dedicating
            internal hours against a managed EASM platform, this guide breaks down the real numbers
            so you can make an informed decision.
          </p>

          <div className="guide-hero-bullets">
            <div className="guide-hero-bullets-title">What You'll Learn</div>
            <ul>
              <li>The tools required for thorough manual reconnaissance</li>
              <li>Time cost per domain: a step-by-step breakdown</li>
              <li>Skill requirements and what they actually cost</li>
              <li>What manual recon structurally misses</li>
              <li>A side-by-side cost comparison with managed EASM</li>
              <li>When manual recon still makes sense (the hybrid approach)</li>
            </ul>
          </div>

          <span className="guide-card-read-time" style={{ display: 'block', marginTop: '12px', marginBottom: '8px' }}>12 min read</span>

          <div className="guide-cta-group">
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Start Free 30-Day Trial</span>
              <span>&rarr;</span>
            </button>
            <Link to="/guides/attack-surface-monitoring" className="da-btn da-btn--secondary">
              Back to Attack Surface Monitoring Guide
            </Link>
          </div>
        </section>

        <nav className="guide-toc">
          <div className="guide-toc-title">In This Guide</div>
          <ul className="guide-toc-list">
            <li><a href="#tools-needed">The Manual Recon Toolchain</a></li>
            <li><a href="#time-cost">Time Cost Per Domain</a></li>
            <li><a href="#skill-requirements">Skill Requirements</a></li>
            <li><a href="#what-manual-misses">What Manual Recon Misses</a></li>
            <li><a href="#easm-alternative">The Managed EASM Alternative</a></li>
            <li><a href="#cost-comparison">Cost Comparison</a></li>
            <li><a href="#when-manual-makes-sense">When Manual Recon Still Makes Sense</a></li>
            <li><a href="#hybrid-approach">The Hybrid Approach</a></li>
            <li><a href="#faq">Frequently Asked Questions</a></li>
          </ul>
        </nav>

        <section id="tools-needed" className="guide-section">
          <h2>The Manual Recon Toolchain</h2>

          <p>
            Running a thorough manual reconnaissance engagement against a single domain requires
            proficiency with multiple open-source tools. Each covers a different phase of
            discovery, and no single tool provides complete coverage.
          </p>

          <h3>Subdomain Enumeration</h3>
          <ul>
            <li><strong>Amass:</strong> OWASP's comprehensive subdomain enumeration engine. Pulls from
                dozens of data sources including certificate transparency logs, DNS brute-forcing, and
                web scraping. Configuration and API key management required for full effectiveness.</li>
            <li><strong>Subfinder:</strong> Fast passive subdomain discovery from ProjectDiscovery. Lighter
                weight than Amass but covers different data sources. Best used in combination.</li>
          </ul>

          <h3>DNS and Infrastructure</h3>
          <ul>
            <li><strong>dig / host:</strong> DNS query tools for resolving records, checking zone
                transfers, and mapping infrastructure. Essential for understanding what lives where.</li>
            <li><strong>httpx:</strong> HTTP probing tool that takes a list of hosts and returns
                live web servers with status codes, titles, technologies, and response data.
                Critical for filtering enumeration output down to real targets.</li>
          </ul>

          <h3>Port Scanning</h3>
          <ul>
            <li><strong>Nmap:</strong> The standard for port scanning and service detection. A full
                TCP scan of common ports across all discovered hosts can take hours depending
                on scope.</li>
          </ul>

          <h3>Vulnerability Scanning</h3>
          <ul>
            <li><strong>Nuclei:</strong> Template-based vulnerability scanner from ProjectDiscovery.
                Over 10,000 templates covering CVEs, misconfigurations, exposed panels, and
                default credentials. Requires understanding of template selection and output
                interpretation.</li>
          </ul>

          <div className="callout callout-info">
            <div className="callout-title">Tool Maintenance Overhead</div>
            <div className="callout-content">
              <p>
                Each of these tools requires ongoing maintenance: version updates, API key rotation,
                configuration tuning, and template updates. Nuclei templates alone receive
                daily updates. Falling behind on tool maintenance means missing new vulnerabilities.
              </p>
            </div>
          </div>
        </section>

        <section id="time-cost" className="guide-section">
          <h2>Time Cost Per Domain</h2>

          <p>
            Here is a realistic breakdown of what thorough manual reconnaissance takes for a
            single domain. These estimates assume an experienced operator who has the tools
            installed, configured, and ready to run.
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Phase</th>
                  <th>Activities</th>
                  <th>Time Estimate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Subdomain Enumeration</td>
                  <td>Run Amass + Subfinder, deduplicate, resolve live hosts with httpx</td>
                  <td>30 - 60 min</td>
                </tr>
                <tr>
                  <td>Port Scanning</td>
                  <td>Nmap service detection on discovered hosts (top 1000 ports minimum)</td>
                  <td>20 - 40 min</td>
                </tr>
                <tr>
                  <td>Vulnerability Scanning</td>
                  <td>Nuclei with appropriate template sets against all live endpoints</td>
                  <td>60 - 120 min</td>
                </tr>
                <tr>
                  <td>Analysis and Reporting</td>
                  <td>Correlate findings, validate, triage severity, write up actionable results</td>
                  <td>60 - 120 min</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Total Per Domain</td>
                  <td style={{ fontWeight: 'bold' }}>One complete reconnaissance cycle</td>
                  <td style={{ fontWeight: 'bold' }}>4 - 8 hours</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            That is 4 to 8 hours for a single domain, for a single point-in-time snapshot.
            Next week your attack surface will have changed — new subdomains, rotated certificates,
            updated services — and the entire process needs to run again.
          </p>

          <div className="callout callout-info">
            <div className="callout-title">Multi-Domain Reality</div>
            <div className="callout-content">
              <p>
                Most organizations have more than one domain. If you manage 5 domains,
                you are looking at 20 to 40 hours per reconnaissance cycle. At weekly
                cadence, that is a full-time position dedicated entirely to reconnaissance.
              </p>
            </div>
          </div>
        </section>

        <section id="skill-requirements" className="guide-section">
          <h2>Skill Requirements</h2>

          <div className="callout callout-warning">
            <div className="callout-title">The Expertise Gap</div>
            <div className="callout-content">
              <p>
                Running the tools is only half the challenge. <strong>Interpreting the output,
                correlating findings across tools, and prioritizing results requires significant
                security expertise.</strong> An Nmap scan produces raw data. Turning that data
                into actionable intelligence requires understanding of protocols, services,
                common attack patterns, and business context.
              </p>
            </div>
          </div>

          <p>
            Effective manual reconnaissance requires proficiency across multiple disciplines:
          </p>

          <ul>
            <li><strong>DNS and networking fundamentals:</strong> Understanding record types, zone
                transfers, CIDR notation, routing, and how infrastructure maps to business
                assets.</li>
            <li><strong>Tool-specific expertise:</strong> Each tool has its own configuration,
                flags, output formats, and quirks. Amass alone has dozens of configuration
                options that affect completeness.</li>
            <li><strong>Vulnerability assessment:</strong> Interpreting Nuclei output requires
                understanding CVSS scoring, exploit maturity, and environmental context to
                triage findings accurately.</li>
            <li><strong>Correlation skills:</strong> Connecting a subdomain found by Amass to
                a service found by Nmap to a vulnerability found by Nuclei — and understanding
                the combined risk — is where real expertise lives.</li>
            <li><strong>Reporting:</strong> Translating technical findings into actionable
                recommendations that non-security stakeholders can act on.</li>
          </ul>

          <p>
            This skill set commands $150 to $300+ per hour for external consultants, or a
            $120K to $180K annual salary for a full-time security engineer. The talent
            market for these skills is extremely competitive.
          </p>
        </section>

        <section id="what-manual-misses" className="guide-section">
          <h2>What Manual Recon Structurally Misses</h2>

          <p>
            Even with expert operators and the best tools, manual reconnaissance has
            fundamental structural limitations that no amount of skill can overcome:
          </p>

          <h3>No Continuous Monitoring</h3>
          <p>
            Manual recon is a point-in-time snapshot. Between runs, your attack surface
            changes: new subdomains are created, certificates expire, services are deployed,
            and configurations drift. A weekly manual cycle means up to 7 days of blind
            spots. A monthly cycle means 30 days.
          </p>

          <h3>No Drift Detection</h3>
          <p>
            Manual recon tells you what exists right now. It does not tell you what
            changed since last time. Without automated baselines and comparison, you
            cannot detect drift — a new port opening, a certificate downgrade, a
            technology version change. These changes are often the earliest indicators
            of misconfiguration or compromise.
          </p>

          <h3>No Historical Trending</h3>
          <p>
            Manual recon produces a report. It does not produce a trend line. Without
            historical data, you cannot answer questions like: Is our attack surface
            growing or shrinking? Are we remediating faster than new issues appear?
            What is our risk trajectory over the last 90 days?
          </p>

          <h3>No Automated Alerting</h3>
          <p>
            When a critical change happens — a new RDP port opens, an SSL certificate
            expires, a new subdomain appears — manual recon will not tell you until
            the next scheduled run. Automated EASM platforms detect these changes
            within hours and alert immediately.
          </p>

          <div className="example-box">
            <div className="example-title">The Gap Between Scans</div>
            <div className="example-content">
              <p>
                Consider this scenario: a developer deploys a staging server on a Friday
                afternoon with default credentials and an exposed admin panel. Your
                monthly manual recon runs on the first of the month.
              </p>
              <p>
                <strong>That server sits exposed for up to 30 days before you know about it.</strong> An
                EASM platform running daily scans would flag it within 24 hours. With drift
                detection, you would receive an alert the moment it appeared.
              </p>
            </div>
          </div>
        </section>

        <section id="easm-alternative" className="guide-section">
          <h2>The Managed EASM Alternative</h2>

          <p>
            A managed EASM platform automates the reconnaissance cycle and adds capabilities
            that manual processes cannot replicate. Here is a direct comparison:
          </p>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Manual Recon</th>
                  <th>Managed EASM</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Time per domain</td>
                  <td>4 - 8 hours</td>
                  <td>3 - 5 minutes (automated)</td>
                </tr>
                <tr>
                  <td>Scan frequency</td>
                  <td>Weekly to monthly (practical limit)</td>
                  <td>Daily to continuous</td>
                </tr>
                <tr>
                  <td>Coverage consistency</td>
                  <td>Depends on operator</td>
                  <td>Identical every run</td>
                </tr>
                <tr>
                  <td>Drift detection</td>
                  <td>Manual diff (if done at all)</td>
                  <td>Automatic baseline comparison</td>
                </tr>
                <tr>
                  <td>Historical trending</td>
                  <td>Manual spreadsheet tracking</td>
                  <td>Built-in dashboards and analytics</td>
                </tr>
                <tr>
                  <td>Alerting</td>
                  <td>Email after manual analysis</td>
                  <td>Real-time alerts (email, Slack, webhook)</td>
                </tr>
                <tr>
                  <td>Skill requirement</td>
                  <td>Senior security engineer</td>
                  <td>IT administrator can operate</td>
                </tr>
                <tr>
                  <td>Tool maintenance</td>
                  <td>Ongoing (updates, configs, API keys)</td>
                  <td>Managed by vendor</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="cost-comparison" className="guide-section">
          <h2>Cost Comparison</h2>

          <p>
            Let's put real numbers to the comparison. These calculations use conservative
            estimates for both approaches.
          </p>

          <h3>Manual Recon: The Math</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Variable</th>
                  <th>Value</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Hours per domain per cycle</td>
                  <td>6 hours (midpoint)</td>
                  <td>Includes scanning, analysis, reporting</td>
                </tr>
                <tr>
                  <td>Frequency</td>
                  <td>Weekly</td>
                  <td>Minimum for meaningful coverage</td>
                </tr>
                <tr>
                  <td>Hourly rate (consultant)</td>
                  <td>$200 / hour</td>
                  <td>Mid-range for qualified security consultant</td>
                </tr>
                <tr>
                  <td>Domains</td>
                  <td>1</td>
                  <td>Single domain baseline</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Monthly cost (1 domain)</td>
                  <td style={{ fontWeight: 'bold' }}>$4,800 / month</td>
                  <td style={{ fontWeight: 'bold' }}>6 hrs x 4 weeks x $200/hr</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Annual cost (1 domain)</td>
                  <td style={{ fontWeight: 'bold' }}>$57,600 / year</td>
                  <td style={{ fontWeight: 'bold' }}>Weekly cadence, 48 working weeks</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p>
            Using internal staff instead of consultants? A security engineer spending 6 hours
            per week on recon for one domain is dedicating 15% of their time to a task that
            could be automated. At a fully loaded cost of $150K per year, that is roughly
            $22,500 in opportunity cost — time that could be spent on remediation, architecture
            review, or incident response.
          </p>

          <h3>Managed EASM: The Math</h3>
          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Variable</th>
                  <th>Value</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>DriftAlarm Standard</td>
                  <td>$99 / month</td>
                  <td>1 domain + 1 IP, daily automated scans</td>
                </tr>
                <tr>
                  <td>Scan frequency</td>
                  <td>Daily (automated)</td>
                  <td>Vulnerability scans and domain discovery run daily; IP and CIDR discovery weekly</td>
                </tr>
                <tr>
                  <td>Setup time</td>
                  <td>3 minutes</td>
                  <td>Add your domain, first scan runs immediately</td>
                </tr>
                <tr>
                  <td>Ongoing operator time</td>
                  <td>~30 min / week</td>
                  <td>Review findings, triage alerts</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 'bold' }}>Annual cost</td>
                  <td style={{ fontWeight: 'bold' }}>$999 / year</td>
                  <td style={{ fontWeight: 'bold' }}>Annual plan pricing</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-info">
            <div className="callout-title">The ROI Is Clear</div>
            <div className="callout-content">
              <p>
                <strong>$999 per year for daily automated coverage vs. $4,800+ per month for weekly
                manual recon.</strong> The managed EASM approach delivers higher-frequency coverage
                at a fraction of the cost — with drift detection, alerting, and trending that
                manual recon cannot provide at any price.
              </p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <div className="guide-cta-group" style={{ marginTop: '20px', marginBottom: '20px' }}>
            <button className="da-btn da-btn--primary" onClick={startTrial}>
              <span>Replace 8 Hours of Manual Recon with a 3-Minute Scan</span>
              <span>&rarr;</span>
            </button>
          </div>
        </section>

        <section id="when-manual-makes-sense" className="guide-section">
          <h2>When Manual Recon Still Makes Sense</h2>

          <p>
            Manual reconnaissance is not obsolete. There are scenarios where human-driven
            recon remains the right approach — or the only approach:
          </p>

          <h3>Penetration Testing</h3>
          <p>
            Penetration tests require creative, adversarial thinking that goes beyond
            automated scanning. Manual recon during a pentest includes social engineering
            reconnaissance, business logic analysis, and targeted exploitation that
            EASM platforms are not designed to perform. Pentests and EASM serve
            different purposes and complement each other.
          </p>

          <h3>Targeted Research</h3>
          <p>
            Investigating a specific asset, tracking a particular threat actor's
            infrastructure, or performing deep OSINT on a merger target requires
            human judgment and adaptive methodology that automated tools cannot
            replicate. This is research, not monitoring.
          </p>

          <h3>Learning and Skill Development</h3>
          <p>
            For security professionals building their skills, manual recon is
            essential training. Understanding what the tools do at a fundamental
            level makes you better at interpreting automated results, configuring
            platforms, and knowing when automated findings need manual investigation.
          </p>

          <h3>Scope Validation</h3>
          <p>
            Before onboarding assets into an EASM platform, a manual recon pass
            can help validate scope, identify edge cases, and ensure your asset
            inventory is complete. This is a one-time activity that feeds into
            ongoing automated monitoring.
          </p>
        </section>

        <section id="hybrid-approach" className="guide-section">
          <h2>The Hybrid Approach: Best of Both Worlds</h2>

          <p>
            The most effective security programs combine automated EASM for continuous
            coverage with manual expertise for deep-dive investigation. Here is how to
            structure the hybrid model:
          </p>

          <h3>Layer 1: Automated EASM (Daily)</h3>
          <ul>
            <li>Continuous asset discovery and monitoring</li>
            <li>Daily vulnerability scanning across all domains and IPs</li>
            <li>Automated drift detection with real-time alerting</li>
            <li>Historical trending and risk score tracking</li>
            <li>Baseline coverage that never misses a cycle</li>
          </ul>

          <h3>Layer 2: Manual Deep Dives (As Needed)</h3>
          <ul>
            <li>Investigate high-priority EASM findings that need validation</li>
            <li>Perform targeted recon on new acquisitions or business changes</li>
            <li>Annual or quarterly penetration testing</li>
            <li>OSINT and threat intelligence for specific concerns</li>
            <li>Custom testing that goes beyond automated template coverage</li>
          </ul>

          <div className="guide-table-wrapper">
            <table className="guide-table">
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>Approach</th>
                  <th>Frequency</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Attack surface monitoring</td>
                  <td>Automated EASM</td>
                  <td>Daily</td>
                </tr>
                <tr>
                  <td>Vulnerability scanning</td>
                  <td>Automated EASM</td>
                  <td>Daily</td>
                </tr>
                <tr>
                  <td>Drift detection and alerting</td>
                  <td>Automated EASM</td>
                  <td>Continuous</td>
                </tr>
                <tr>
                  <td>Finding validation</td>
                  <td>Manual (triggered by EASM alerts)</td>
                  <td>As needed</td>
                </tr>
                <tr>
                  <td>Penetration testing</td>
                  <td>Manual</td>
                  <td>Quarterly or annually</td>
                </tr>
                <tr>
                  <td>New asset recon</td>
                  <td>Manual (then add to EASM)</td>
                  <td>Ad hoc</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="callout callout-info">
            <div className="callout-title">Let Automation Handle the Baseline</div>
            <div className="callout-content">
              <p>
                The hybrid approach lets your security team focus their expertise where it
                matters most — investigating real findings, validating complex vulnerabilities,
                and making strategic decisions — instead of spending hours running the same
                scans every week. <strong>Automate the routine. Reserve human expertise for
                the work that requires it.</strong>
              </p>
            </div>
          </div>
        </section>

        <section className="guide-section">
          <h2>Related Security Guides</h2>
          <p>
            Continue building your understanding of EASM and attack surface management:
          </p>
          <ul>
            <li>
              <Link to="/guides/easm-vs-vulnerability-scanning">EASM vs Vulnerability Scanning</Link> — Understand the difference between EASM and traditional vulnerability scanners, and why you likely need both.
            </li>
            <li>
              <Link to="/guides/attack-surface-checklist">Attack Surface Checklist</Link> — A practical checklist for auditing your external attack surface, covering domains, IPs, certificates, and exposed services.
            </li>
            <li>
              <Link to="/guides/vulnerability-validation">Vulnerability Validation Guide</Link> — Learn how to validate scanner findings with copy-paste commands before escalating to remediation.
            </li>
            <li>
              <Link to="/guides/attack-surface-monitoring">Attack Surface Monitoring Guide</Link> — The complete guide to scoping, discovering, and prioritizing your external attack surface with a 7-day quickstart.
            </li>
            <li>
              <Link to="/external-attack-surface-management">External Attack Surface Management</Link> — See how DriftAlarm provides continuous EASM with automated discovery, scanning, drift detection, and alerting.
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2>Summary</h2>
          <p>
            Manual reconnaissance is a respected discipline and a valuable skill set. But as a
            primary monitoring strategy, it is expensive, inconsistent, and structurally unable
            to provide continuous coverage. The math is straightforward: 4 to 8 hours per
            domain per cycle, multiplied by the number of domains and the frequency required,
            quickly exceeds the cost of an automated platform that runs daily.
          </p>
          <p>
            The answer is not to abandon manual recon. The answer is to stop using it for
            baseline monitoring and start using it where it adds the most value: targeted
            investigation, penetration testing, and validating automated findings. Let EASM
            handle the continuous coverage. Let your team handle the work that requires
            human judgment.
          </p>

          <div className="guide-cta-group" style={{ marginTop: '40px' }}>
            <Link to="/upgrade" className="da-btn da-btn--primary">
              <span>Start Automating Your Attack Surface Monitoring</span>
              <span>&rarr;</span>
            </Link>
          </div>
        </section>
        <GuideFAQ path="/guides/manual-recon-vs-easm" />
      </main>

      <PublicFooter />
    </div>
  );
}

export default ManualReconvsEASMGuide;
